import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { HardHat, Home, Trash2, MailPlus, RefreshCw, BadgeDollarSign, KeyRound, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ColonyBadge } from "@/lib/colonyColors";

/**
 * Users tab on the Management USA NJ portal — fully separate from the
 * Catskills Pool Supplies user list. Workers (shared users collection,
 * excluded from the pool page) + colony-portal logins (own collection).
 */
export default function MgmtUsers() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [logins, setLogins] = useState(null); // null = hidden (not admin)
  const [newLogin, setNewLogin] = useState({ name: "", email: "" });
  const [inviting, setInviting] = useState(false);

  const load = useCallback(() => {
    api.get("/mgmt/users").then((r) => setData(r.data)).catch((e) => {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    });
    api.get("/mgmt/admin-logins").then((r) => setLogins(r.data)).catch(() => setLogins(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleManager = async (w) => {
    setBusyId(w.id);
    try {
      const next = !w.is_worker_manager;
      await api.patch(`/mgmt/worker-users/${w.id}/manager`, { is_worker_manager: next });
      setData((d) => ({ ...d, workers: d.workers.map((x) => x.id === w.id ? { ...x, is_worker_manager: next } : x) }));
      toast.success(next ? "Worker manager enabled." : "Worker manager disabled.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const toggleDisabled = async (w) => {
    setBusyId(w.id);
    try {
      const next = !w.disabled;
      await api.patch(`/users/${w.id}/status`, { disabled: next });
      setData((d) => ({ ...d, workers: d.workers.map((x) => x.id === w.id ? { ...x, disabled: next } : x) }));
      toast.success(next ? "Account disabled — they can't sign in." : "Account re-enabled.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const deleteWorker = async (w) => {
    if (!window.confirm(`Delete ${w.name || w.email}'s login? They will no longer be able to sign in. Payroll history stays.`)) return;
    setBusyId(w.id);
    try {
      await api.delete(`/users/${w.id}`);
      setData((d) => ({ ...d, workers: d.workers.filter((x) => x.id !== w.id) }));
      toast.success("Worker login deleted.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const resendInvite = async (u) => {
    const cid = u.colonies[0]?.id;
    if (!cid) return toast.error("This login isn't attached to any colony.");
    setBusyId(u.id);
    try {
      const { data: r } = await api.post(`/mgmt/colonies/${cid}/users/${u.id}/resend-invite`);
      if (r.email_sent) toast.success(`Invite emailed to ${u.email}.`);
      else toast.error(r.email_error || "Email could not be sent.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const deleteColonyUser = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}'s colony portal login completely (all colonies)?`)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/mgmt/colony-users/${u.id}`);
      setData((d) => ({ ...d, colony_users: d.colony_users.filter((x) => x.id !== u.id) }));
      toast.success("Colony login removed.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const inviteLogin = async (e) => {
    e.preventDefault();
    if (!newLogin.email.trim()) return;
    setInviting(true);
    try {
      const { data: r } = await api.post("/mgmt/admin-logins", newLogin);
      setLogins((l) => [r.user, ...(l || []).filter((x) => x.id !== r.user.id)]);
      setNewLogin({ name: "", email: "" });
      if (r.already_active) toast.success(`${r.user.email} now has management access — they sign in with their existing password.`);
      else if (r.email_sent) toast.success(`Invite emailed to ${r.user.email}.`);
      else toast.error(r.email_error || "Login created but the invite email could not be sent.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setInviting(false); }
  };

  const resendLoginInvite = async (u) => {
    setBusyId(u.id);
    try {
      const { data: r } = await api.post(`/mgmt/admin-logins/${u.id}/resend`);
      if (r.email_sent) toast.success(`Invite emailed to ${u.email}.`);
      else toast.error(r.email_error || "Email could not be sent.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const toggleLoginDisabled = async (u) => {
    setBusyId(u.id);
    try {
      const next = !u.disabled;
      await api.patch(`/mgmt/admin-logins/${u.id}`, { disabled: next });
      setLogins((l) => l.map((x) => (x.id === u.id ? { ...x, disabled: next } : x)));
      toast.success(next ? "Login disabled." : "Login re-enabled.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const deleteLogin = async (u) => {
    const msg = u.linked_role
      ? `Remove ${u.name || u.email}'s management portal access? Their existing ${u.linked_role} account stays untouched.`
      : `Delete ${u.name || u.email}'s admin portal login?`;
    if (!window.confirm(msg)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/mgmt/admin-logins/${u.id}`);
      setLogins((l) => l.filter((x) => x.id !== u.id));
      toast.success("Login deleted.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  if (!data) return <p className="text-sm text-muted-foreground py-6">Loading…</p>;

  return (
    <div className="space-y-8 min-w-0" data-testid="mgmt-users-tab">
      <div className="flex items-center justify-between gap-3 mobile-stack">
        <p className="text-sm text-muted-foreground">
          Management USA NJ people only — fully separate from the Catskills Pool Supplies users list.
        </p>
        <Button variant="outline" size="sm" className="rounded-sm mobile-full" onClick={load} data-testid="mgmt-users-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
        </Button>
      </div>

      {logins !== null && (
        <section>
          <h3 className="font-bold flex items-center gap-2 mb-2">
            <KeyRound className="w-4 h-4" /> Admin portal logins <span className="text-muted-foreground font-normal text-sm">({logins.length})</span>
          </h3>
          <p className="text-xs text-muted-foreground mb-2">
            Full access to this Management USA NJ portal only — they can never see the Catskills Pool Supplies side. They sign in at /managementusa.
          </p>
          <form onSubmit={inviteLogin} className="grid grid-cols-1 sm:grid-cols-[11rem_1fr_auto] gap-2 mb-3">
            <Input
              className="rounded-sm h-11 sm:h-9 w-full"
              placeholder="Name"
              value={newLogin.name}
              onChange={(e) => setNewLogin((s) => ({ ...s, name: e.target.value }))}
              data-testid="mgmt-login-name-input"
            />
            <Input
              className="rounded-sm h-11 sm:h-9 w-full"
              type="email"
              required
              placeholder="Email"
              value={newLogin.email}
              onChange={(e) => setNewLogin((s) => ({ ...s, email: e.target.value }))}
              data-testid="mgmt-login-email-input"
            />
            <Button type="submit" size="sm" className="rounded-sm h-11 sm:h-9" disabled={inviting} data-testid="mgmt-login-invite-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> {inviting ? "Sending…" : "Invite"}
            </Button>
          </form>
          <div className="border border-border rounded-sm overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold bg-secondary/30 border-b border-border">
              <div className="col-span-5">Name / email</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3">Active</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {logins.length === 0 && (
              <p className="text-sm text-muted-foreground px-3 py-5 text-center">No admin portal logins yet — invite one above.</p>
            )}
            {logins.map((u) => (
              <div key={u.id} className="admin-responsive-row grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-border last:border-b-0 items-center" data-testid={`mgmt-login-row-${u.id}`}>
                <div className="col-span-12 md:col-span-5 min-w-0" data-mobile-label="Login" data-mobile-span="full">
                  <div className={`font-medium text-sm truncate ${u.disabled ? "line-through text-muted-foreground" : ""}`}>{u.name || "—"}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                <div className="col-span-4 md:col-span-2" data-mobile-label="Status">
                  <Badge variant="outline" className={`rounded-sm text-[10px] ${u.has_password ? "text-emerald-700" : "text-amber-600"}`}>
                    {u.has_password ? "Active" : "Invite pending"}
                  </Badge>
                  {u.linked_role && (
                    <Badge variant="outline" className="rounded-sm text-[10px] ml-1 text-sky-700" title={`Also has a ${u.linked_role} account — management access was added on top.`}>
                      Linked · {u.linked_role}
                    </Badge>
                  )}
                </div>
                <div className="col-span-3 md:col-span-3" data-mobile-label="Active">
                  {u.linked_role
                    ? <span className="text-xs text-muted-foreground">—</span>
                    : <Switch checked={!u.disabled} onCheckedChange={() => toggleLoginDisabled(u)} disabled={busyId === u.id} data-testid={`mgmt-login-active-${u.id}`} />}
                </div>
                <div className="col-span-5 md:col-span-2 text-right whitespace-nowrap mobile-card-actions" data-mobile-label="Actions">
                  <Button variant="ghost" size="sm" className="rounded-sm h-8" onClick={() => resendLoginInvite(u)} disabled={busyId === u.id} data-testid={`mgmt-login-resend-${u.id}`} title="Resend invite email">
                    <MailPlus className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-sm h-8 text-destructive" onClick={() => deleteLogin(u)} disabled={busyId === u.id} data-testid={`mgmt-login-delete-${u.id}`} title="Delete login">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="font-bold flex items-center gap-2 mb-2">
          <HardHat className="w-4 h-4" /> Workers <span className="text-muted-foreground font-normal text-sm">({data.workers.length})</span>
        </h3>
        <div className="border border-border rounded-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold bg-secondary/30 border-b border-border">
            <div className="col-span-4">Name / email</div>
            <div className="col-span-3">Worker manager</div>
            <div className="col-span-3">Active</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {data.workers.length === 0 && (
            <p className="text-sm text-muted-foreground px-3 py-5 text-center">No worker logins yet — create them from the Payroll tab.</p>
          )}
          {data.workers.map((w) => (
            <div key={w.id} className="admin-responsive-row grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-border last:border-b-0 items-center" data-testid={`mgmt-user-worker-${w.id}`}>
              <div className="col-span-12 md:col-span-4 min-w-0" data-mobile-label="Worker" data-mobile-span="full">
                <div className={`font-medium text-sm truncate ${w.disabled ? "line-through text-muted-foreground" : ""}`}>{w.name || "—"}</div>
                <div className="text-xs text-muted-foreground truncate">{w.email}</div>
              </div>
              <div className="col-span-6 md:col-span-3 flex items-center gap-2" data-mobile-label="Manager">
                <Switch checked={w.is_worker_manager} onCheckedChange={() => toggleManager(w)} disabled={busyId === w.id} data-testid={`mgmt-user-manager-${w.id}`} />
                {w.is_worker_manager && <Badge variant="outline" className="rounded-sm text-[10px]"><BadgeDollarSign className="w-3 h-3 mr-1" /> Manager</Badge>}
              </div>
              <div className="col-span-4 md:col-span-3" data-mobile-label="Active">
                <Switch checked={!w.disabled} onCheckedChange={() => toggleDisabled(w)} disabled={busyId === w.id} data-testid={`mgmt-user-active-${w.id}`} />
              </div>
              <div className="col-span-2 md:col-span-2 text-right mobile-card-actions" data-mobile-label="Actions">
                <Button variant="ghost" size="sm" className="rounded-sm h-8 text-destructive" onClick={() => deleteWorker(w)} disabled={busyId === w.id} data-testid={`mgmt-user-delete-${w.id}`} title="Delete login">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-bold flex items-center gap-2 mb-2">
          <Home className="w-4 h-4" /> Colony portal users <span className="text-muted-foreground font-normal text-sm">({data.colony_users.length})</span>
        </h3>
        <div className="border border-border rounded-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold bg-secondary/30 border-b border-border">
            <div className="col-span-4">Name / email</div>
            <div className="col-span-4">Colonies</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {data.colony_users.length === 0 && (
            <p className="text-sm text-muted-foreground px-3 py-5 text-center">No colony portal logins yet — add them from a colony&apos;s card on the Payroll tab.</p>
          )}
          {data.colony_users.map((u) => (
            <div key={u.id} className="admin-responsive-row grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-border last:border-b-0 items-center" data-testid={`mgmt-user-colony-${u.id}`}>
              <div className="col-span-12 md:col-span-4 min-w-0" data-mobile-label="User" data-mobile-span="full">
                <div className="font-medium text-sm truncate">{u.name || "—"}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
              </div>
              <div className="col-span-6 md:col-span-4 flex flex-wrap gap-1" data-mobile-label="Colonies">
                {u.colonies.map((c) => <ColonyBadge key={c.id} id={c.id} name={c.name} />)}
                {u.colonies.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
              </div>
              <div className="col-span-3 md:col-span-2" data-mobile-label="Status">
                <Badge variant="outline" className={`rounded-sm text-[10px] ${u.has_password ? "text-emerald-700" : "text-amber-600"}`}>
                  {u.has_password ? "Active" : "Invite pending"}
                </Badge>
              </div>
              <div className="col-span-3 md:col-span-2 text-right whitespace-nowrap mobile-card-actions" data-mobile-label="Actions">
                <Button variant="ghost" size="sm" className="rounded-sm h-8" onClick={() => resendInvite(u)} disabled={busyId === u.id} data-testid={`mgmt-user-invite-${u.id}`} title="Resend invite email">
                  <MailPlus className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="rounded-sm h-8 text-destructive" onClick={() => deleteColonyUser(u)} disabled={busyId === u.id} data-testid={`mgmt-user-colony-delete-${u.id}`} title="Remove login">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
